<template>
  <div>
    <vs-popup
      title="Sincronização de Mensagens"
      :button-close-hidden="false"
      :active.sync="isActive"
      @close="closePopUpSyncMessages()"
      id="sync-messages-popup"
    >
      <div class="sync-messages-modal">
        <div class="sync-messages-modal__header">
          <h5 class="sync-messages-modal__title">Defina o período da sincronização</h5>
          <p class="sync-messages-modal__subtitle">
            Quanto maior a quantidade de dias, maior a carga na infraestrutura. Use sempre o menor intervalo possível.
          </p>
        </div>

        <div v-if="!instance.activated" class="sync-messages-section sync-messages-section--danger">
          <p class="sync-messages-impact__title">Conexão desconectada</p>
          <p class="sync-messages-impact__text">
            A conexão está desconectada, a solicitação não será acatada. Caso já tenha conectado, atualize a listagem de conexões no menu anterior.
          </p>
        </div>

        <div class="sync-messages-section">
          <label class="vs-input--label">Quantidade de dias</label>
          <v-select
            v-model="selectedQuantityDays"
            class="w-full"
            :options="quantityDays"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
            :clearable="false"
            :searchable="false"
          />

          <div :class="['sync-messages-impact', impactClass]">
            <p class="sync-messages-impact__title">{{ impactTitle }}</p>
            <p class="sync-messages-impact__text">{{ impactText }}</p>
          </div>
        </div>


        <div class="sync-messages-advanced">
          <button
            type="button"
            class="sync-messages-advanced__toggle"
            @click="showAdvanced = !showAdvanced"
          >
            <span>Avançado</span>
            <feather-icon
              :icon="showAdvanced ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              svgClasses="w-4 h-4"
            />
          </button>

          <div class="sync-messages-advanced__content" v-if="showAdvanced">
            <div class="sync-messages-option">
              <vs-checkbox v-model="unlimitedSync" size="small">
                <span class="sync-messages-option__title">Sincronização Ilimitada</span>
              </vs-checkbox>
              <p class="sync-messages-option__description">
                Use somente em cenários excepcionais de migração para Cloud. Essa opção remove os limites da sincronização e pode gerar carga crítica na infraestrutura.
              </p>
            </div>
          </div>
        </div>

        <div class="sync-messages-modal__actions">
          <span>
            <vs-button
              size="medium"
              color="danger"
              type="border"
              @click="closePopUpSyncMessages()"
            >
              <span> Cancelar </span>
            </vs-button>
          </span>
          <span>
            <vs-button
              class="ml-4"
              size="medium"
              color="success"
              :disabled="!instance.activated"
              @click="syncMessagesInstance"
            >
              <span> Sincronizar </span>
            </vs-button>
          </span>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import vSelect from "vue-select";
import instanceHttp from "./../../http/requests/connections/index";

export default {
  name: "SyncMessagesInstance",
  components: {
    vSelect,
  },
  data() {
    return {
      selectedQuantityDays: {
        label: "5",
        value: 5,
      },
      quantityDays: [
        {
          label: "5",
          value: 5,
        },
        {
          label: "15",
          value: 15,
        },
        {
          label: "30",
          value: 30,
        },
        {
          label: "60",
          value: 60,
        },
        {
          label: "120",
          value: 120,
        },
      ],
      unlimitedSync: false,
      showAdvanced: false,
    };
  },
  props: {
    isActive: {
      type: Boolean,
      required: true,
    },
    instance: {
      type: Object | Array,
      required: true,
    },
    tenantName: {
      type: String,
      required: true,
    },
  },
  emits: ["isActiveFalse", "successfullyUpdated"],
  watch: {
    isActive(value) {
      if (value) {
        this.unlimitedSync = false;
        this.showAdvanced = false;
        this.selectedQuantityDays = {label: "5", value: 5}
      }
    }
  },
  computed: {
    selectedDays() {
      return Number(this.selectedQuantityDays && this.selectedQuantityDays.value ? this.selectedQuantityDays.value : 0);
    },
    impactClass() {
      if (this.selectedDays >= 120) {
        return "sync-messages-impact--critical";
      }

      if (this.selectedDays >= 60) {
        return "sync-messages-impact--high";
      }

      if (this.selectedDays >= 30) {
        return "sync-messages-impact--medium";
      }

      return "sync-messages-impact--low";
    },
    impactTitle() {
      if (this.selectedDays >= 120) {
        return "Impacto crítico na infraestrutura";
      }

      if (this.selectedDays >= 60) {
        return "Impacto alto na infraestrutura";
      }

      if (this.selectedDays >= 30) {
        return "Impacto moderado na infraestrutura";
      }

      return "Impacto controlado";
    },
    impactText() {
      if (this.selectedDays >= 120) {
        return "120 dias é um valor alto e deve ser usado apenas quando estritamente necessário, pois a carga aumenta de forma significativa.";
      }

      if (this.selectedDays >= 60) {
        return "Esse volume já eleva bastante o processamento e deve ser usado com cautela para evitar pressão desnecessária na infraestrutura.";
      }

      if (this.selectedDays >= 30) {
        return "O consumo de recursos cresce junto com o período selecionado. Confirme se esse intervalo é realmente necessário.";
      }

      return "Mesmo em períodos menores, a sincronização consome infraestrutura. Prefira sempre o menor intervalo possível.";
    },
  },
  methods: {
    verify() {
      const permissions = localStorage.getItem("permissions");
      const twoFactor = localStorage.getItem("twoFactor");
      if (!permissions && !twoFactor) {
        this.$router.push("/user-settings");
      } else if (!permissions && twoFactor) {
        this.$router.push("/two-factor");
      }
    },
    async syncMessagesInstance() {
      if (!this.instance.activated) {
        return;
      }

      await instanceHttp.syncMessages(
        this.instance.id,
        this.selectedQuantityDays.value,
        localStorage.getItem("accessToken"),
        this.tenantName,
        this.unlimitedSync
      ).then((response) => {
        if (response.data.data.syncInstanceChats) {
          this.$vs.notify({
            title: "Sucesso!",
            text: "Sincronização de mensagem executada com sucesso!",
            color: "success",
          });
        } else {
          this.$vs.notify({
            title: "Erro!",
            text: "Algo deu errado ao tentar sincronizar as mensagens!",
            color: "warning",
          });
        }

        return this.$emit("successfullyUpdated");
      }).catch((exception) => {
        console.error(exception);
        this.$vs.notify({
          title: "Erro!",
          text: "Algo deu errado ao tentar sincronizar as mensagens!",
          color: "danger",
        });
      });
    },
    closePopUpSyncMessages() {
      this.unlimitedSync = false;
      this.showAdvanced = false;
      this.$emit("isActiveFalse");
    },
  },
  mounted() {
    this.verify();
  },
  created() {
    this.$events.on("closePopUpSyncMessages", this.closePopUpSyncMessages);
  },
  beforeDestroy() {
    this.$events.off("closePopUpSyncMessages", this.closePopUpSyncMessages);
  },
};
</script>

<style lang="scss">
#sync-messages-popup {
  div.vs-popup {
    .vs-popup--content {
      padding: 18px;
      margin: 0;
    }

    margin: 0;
    border-color: rgba(18, 17, 17, 0.769);
    width: 560px !important;
  }
}

.sync-messages-modal__header {
  margin-bottom: 16px;
}

.sync-messages-modal__title {
  color: #4b4b4b;
  margin-bottom: 6px;
}

.sync-messages-modal__subtitle {
  color: #7a7a7a;
  font-size: 0.92rem;
  line-height: 1.4;
  margin: 0;
}

.sync-messages-section {
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 14px;
  background: #fafafa;
}

.sync-messages-section + .sync-messages-section {
  margin-top: 14px;
}

.sync-messages-section--danger {
  background: #fff0f0;
  border-color: #ffb1b1;
}

.sync-messages-impact {
  border-radius: 10px;
  margin-top: 14px;
  padding: 12px;
  border: 1px solid #e6e6e6;
  background: #fff;
}

.sync-messages-impact__title {
  font-weight: 600;
  margin-bottom: 6px;
}

.sync-messages-impact__text {
  color: #666;
  font-size: 0.88rem;
  line-height: 1.45;
  margin: 0;
}

.sync-messages-impact--low {
  border-color: #d7ead9;
  background: #f5fbf6;
}

.sync-messages-impact--medium {
  border-color: #ffe1a8;
  background: #fffaf0;
}

.sync-messages-impact--high {
  border-color: #ffc98f;
  background: #fff4e8;
}

.sync-messages-impact--critical {
  border-color: #ffb1b1;
  background: #fff0f0;
}

.sync-messages-advanced {
  margin-top: 14px;
}

.sync-messages-advanced__toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e7e7e7;
  border-radius: 10px;
  padding: 12px 14px;
  background: #fff;
  color: #3e3e3e;
  font-weight: 600;
  cursor: pointer;
}

.sync-messages-advanced__content {
  margin-top: 12px;
}

.sync-messages-option {
  border: 1px solid #e7e7e7;
  border-radius: 10px;
  padding: 14px;
  background: #fafafa;
}

.sync-messages-option__title {
  color: #2f2f2f;
  font-weight: 600;
}

.sync-messages-option__description {
  color: #6c6c6c;
  font-size: 0.88rem;
  line-height: 1.45;
  margin: 8px 0 0 28px;
}

.sync-messages-modal__actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 18px;
}
</style>
